import { getTzHours } from './utils';
import { CREDITS_RESET_HOUR } from '../types/consts/credits';

const ONE_HOUR_MS = 60 * 60 * 1000;

const lastDayOfMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

/**
 * Start of the monthly window `monthsToAdd` months after the anchor, keeping its time of day.
 *
 * A day beyond the length of the target month is capped for that month only: an anchor on the
 * 31st gives Feb 28, then Mar 31 again. That is why normalizing a stored anchor down to 28 was
 * wrong — it lost the real day for every following month.
 *
 * Copied in `mobile/src/features/profile/utils/forfait.ts`, which cannot import this. Change
 * one, change the other.
 */
const monthlyWindowStart = (anchor: Date, monthsToAdd: number) => {
    const date = new Date(anchor.getTime());
    const iAnchorDay = anchor.getDate();

    date.setDate(1); // So changing the month can't overflow into the next one
    date.setMonth(date.getMonth() + monthsToAdd);
    date.setDate(Math.min(iAnchorDay, lastDayOfMonth(date.getFullYear(), date.getMonth())));

    return date.getTime();
};

/**
 * The monthly credits window containing `nowMs`, anchored on `anchorMs`.
 *
 * Credits are monthly whatever the billing interval: an annual subscription paid on January
 * 12th still gets its allocation on the 12th of every month. The window therefore depends only
 * on the anchor's day and time of day, never on the interval — which is what lets the annual
 * and the monthly plans run on the very same code.
 *
 * The time of day matters: the window opens at the anchor's hour, as a Stripe cycle does. A
 * subscription whose cycle falls at 23:30 is still in its previous window at 23:00.
 *
 * @example
 * // anchor = Jan 12 2026 09:00, now = Mar 3 2026 → { start: Feb 12 2026 09:00, end: Mar 12 2026 09:00 }
 */
export const creditsCycleAt = (anchorMs: number, nowMs: number = Date.now()): { start: number; end: number } => {
    const anchor = new Date(anchorMs);
    const now = new Date(nowMs);

    let iMonths = (now.getFullYear() - anchor.getFullYear()) * 12 + (now.getMonth() - anchor.getMonth());

    // The anniversary of this month has not passed yet: we are still in the previous window.
    if (monthlyWindowStart(anchor, iMonths) > nowMs) iMonths -= 1;

    return {
        start: monthlyWindowStart(anchor, iMonths),
        end: monthlyWindowStart(anchor, iMonths + 1),
    };
};

// First nightly reset at or after the window end. Mirrored in mobile/src/features/profile/utils/forfait.ts
export const nextCreditsResetAt = (periodEndMs: number): number => {
    const date = new Date(Math.ceil(periodEndMs / ONE_HOUR_MS) * ONE_HOUR_MS);
    while (getTzHours(date) !== CREDITS_RESET_HOUR) date.setTime(date.getTime() + ONE_HOUR_MS);

    return date.getTime();
};


/** `months` after `fromMs`, keeping the day of month and the time of day. */
export const monthsAfter = (fromMs: number, months: number): number => monthlyWindowStart(new Date(fromMs), months);

/**
 * The end of the engagement sold with a company.
 *
 * Counted from the day the service actually starts, not from the payment: an onboarding pushed
 * back pushes the engagement back with it, so the client is never engaged on days they were
 * not served.
 */
export const commitmentDateFrom = (startMs: number, months: number): number => monthsAfter(startMs, months);

/**
 * The day a number actually stops being served when its cancellation is asked for.
 *
 * The rule is unchanged for a number that carries no engagement: the end of the period already
 * paid. Under engagement, the periods roll forward until one ends on or after the engagement,
 * so the client is never cut mid-engagement nor billed a period beyond it. An engagement
 * landing inside a period therefore runs to the end of that period.
 *
 * @example
 * // monthly, engaged until month 12, cancelled at month 3 → end of month 12
 * // yearly, engaged until month 24, cancelled at month 2  → end of the cycle covering month 24
 */
export const cancellationDateFor = (periodEndMs: number, intervalMonths: number, commitmentMs?: number | null): number => {
    if (!commitmentMs || commitmentMs <= periodEndMs || intervalMonths <= 0) return periodEndMs;

    // Counted from the period end every time, never from the previous result: chaining lets the
    // day-of-month capping stick. An anchor on the 31st becomes the 28th once February is
    // crossed and never comes back, while the real Stripe cycle returns to the 31st — a whole
    // period billed too many on every monthly engagement anchored on the 29th, 30th or 31st.
    let iPeriods = 1;
    let iEnd = monthsAfter(periodEndMs, intervalMonths);

    while (iEnd < commitmentMs) {
        iPeriods += 1;
        iEnd = monthsAfter(periodEndMs, iPeriods * intervalMonths);
    }

    return iEnd;
};

/**
 * Whether going from `oldPlan` to `newPlanId` is an upgrade, or null when neither of its lists
 * allows it. A plan listed both ways is told apart by its price.
 *
 * The single rule of the plan change, shared by the backend and the backoffice modal.
 */
export const isPlanUpgrade = (
    oldPlan: { upgradable_to?: string[]; downgradable_to?: string[]; price?: { value?: number } },
    newPlanId: string,
    newPlan: { price?: { value?: number } },
): boolean | null => {
    const canUpgrade = Array.isArray(oldPlan.upgradable_to) && oldPlan.upgradable_to.includes(newPlanId);
    const canDowngrade = Array.isArray(oldPlan.downgradable_to) && oldPlan.downgradable_to.includes(newPlanId);

    if (!canUpgrade && !canDowngrade) return null;
    if (canUpgrade !== canDowngrade) return canUpgrade;

    return (newPlan.price?.value || 0) > (oldPlan.price?.value || 0);
};

/**
 * The promo a subscription runs at `nowMs`: the one ending soonest, its end excluded, as for the
 * temporary rates. The single rule behind every grant and every display of the allocation.
 * Mirrored in `mobile/src/features/profile/utils/forfait.ts`, which cannot import the libs.
 */
export const runningCreditsPromo = <T extends { ending_at: number; leedbucks: number }>(periods: T[] | undefined, nowMs: number): T | undefined =>
    [...(periods ?? [])].sort((a, b) => a.ending_at - b.ending_at).find((period) => period.ending_at > nowMs);
